import { Button, Checkbox, Dialog } from "@mui/material";
import cx from "classnames";
import React, { useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as TipIcon } from "assets/tip.svg";
import { ReactComponent as XMark } from "assets/x-mark.svg";
import ShotContext from "components/Shot/ShotContext";
import { AiIcon } from "components/TrackingSidepanel";
import {
  SLAPSHOT_MY_PLAN_UPGRADE_URL,
  SLAPSHOT_SUBSCRIPTION_UPGRADE_URL,
} from "constants/urls";
import { getSingularOrPlural } from "utils/helper";
import { useCountdown } from "utils/hooks";

import styles from "./ThrottlePopup.module.scss";

type Layer = {
  id: number;
  name: string;
  cover_image_url: string;
};

type Props = {
  layers: Layer[];
  context: "Export" | "Prediction";
  onClose: () => void;
  onSubmit: (layerIds: number[]) => void;
};

const ThrottlePopup = ({ layers, context, onClose, onSubmit }: Props) => {
  const navigate = useNavigate();

  const { subscription, project, throttleUsage, getThrottleUsage } =
    useContext(ShotContext);
  const [selectedLayers, setSelectedLayers] = React.useState<Layer[]>([]);

  const layersToReduce = useMemo(() => {
    let value = 0;
    if (context === "Export") {
      if (throttleUsage?.is_export_throttled) {
        value = selectedLayers.length - (throttleUsage.allowed_exports || 0);
      }
    } else if (throttleUsage?.is_inference_throttled) {
      value = selectedLayers.length - (throttleUsage.allowed_inferences || 0);
    }
    return value;
  }, [context, selectedLayers.length, throttleUsage]);

  const resetAt = useMemo(() => {
    let value: string | null | undefined = null;
    const layerUsageCount =
      ((context === "Export"
        ? subscription?.plan.export_throttling
        : subscription?.plan.inference_throttling) || 0) -
      ((context === "Export"
        ? throttleUsage?.allowed_exports
        : throttleUsage?.allowed_inferences) || 0);

    if (context === "Export") {
      value = throttleUsage?.full_export_provision_at;
    } else {
      value = throttleUsage?.full_inference_provision_at;
    }
    if (
      value &&
      layerUsageCount > 0 &&
      layersToReduce > 0 &&
      layersToReduce <= layerUsageCount
    ) {
      const timeDiff = new Date(value).getTime() - new Date().getTime();
      if (timeDiff > 0) {
        return new Date(
          new Date().getTime() +
            Math.ceil((timeDiff * layersToReduce) / layerUsageCount),
        );
      }
    }
    return null;
  }, [context, layersToReduce, subscription, throttleUsage]);

  const isDisabled = useMemo(
    () => selectedLayers.length === 0 || layersToReduce > 0,
    [layersToReduce, selectedLayers.length],
  );

  const countdown = useCountdown(resetAt, getThrottleUsage);

  const handleLayerClick = (layer: Layer, isChecked: boolean) => {
    if (isChecked) {
      setSelectedLayers((oldLayers) =>
        Array.from(new Set([...oldLayers, layer])),
      );
    } else {
      setSelectedLayers((oldLayers) =>
        oldLayers.filter((l) => l.id !== layer.id),
      );
    }
  };

  useEffect(() => {
    setSelectedLayers(layers);
  }, [layers]);

  return (
    <Dialog
      open
      slotProps={{
        paper: {
          className: "dialog",
        },
      }}
    >
      <div className="modalContainer">
        <div className="modalHeader">
          <h3 className="modalTitle">{context} Limit Reached</h3>
          <button onClick={onClose} type="button" className="closeButton">
            <XMark />
          </button>
        </div>

        <div className={styles.container}>
          <div className={styles.message}>
            {layersToReduce > 0 && (
              <div>
                {resetAt ? (
                  <span>
                    You need to wait for <strong>{countdown}</strong> before you
                    can {context === "Export" ? "export" : "predict"} these
                    layers.
                  </span>
                ) : (
                  <span>
                    You can upgrade to PRO plan to{" "}
                    {context === "Export" ? "export" : "predict"} these layers.
                  </span>
                )}
                <br />
                <br />
                <span>
                  OR you can reduce <strong>{layersToReduce}</strong>{" "}
                  {getSingularOrPlural(layersToReduce, "layer")} from below to{" "}
                  {context === "Export" ? "export" : "predict"} now.
                </span>
              </div>
            )}
          </div>

          <div className={styles.scrollContainer}>
            <div className={styles.layersGrid}>
              {layers.map((layer) => {
                const checked = selectedLayers.some((l) => l.id === layer.id);
                return (
                  <div
                    key={layer.id}
                    className={styles.layerCon}
                    onClick={() => {
                      handleLayerClick(layer, !checked);
                    }}
                  >
                    <img
                      className={styles.layerPreview}
                      src={layer.cover_image_url}
                      alt={layer.name}
                    />
                    <div className={styles.layerName}>{layer.name}</div>
                    <Checkbox
                      sx={{
                        color: "#E8EAED",
                        "&.Mui-checked": {
                          color: "#6565EC",
                        },
                        "& .MuiSvgIcon-root": { fontSize: 16 },
                        width: "16px",
                        height: "16px",
                      }}
                      onChange={(e, v) => {
                        handleLayerClick(layer, v);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      checked={checked}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          {project.company.is_admin && (
            <div className={styles.tip}>
              <TipIcon />
              <div>
                <span
                  className={styles.upgradeLink}
                  onClick={() => {
                    navigate(
                      project.company.is_owner
                        ? SLAPSHOT_MY_PLAN_UPGRADE_URL
                        : `${SLAPSHOT_SUBSCRIPTION_UPGRADE_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}`,
                    );
                  }}
                >
                  Upgrade to PRO plan
                </span>{" "}
                to have no limits on {context.toLowerCase()}s.
              </div>
            </div>
          )}

          <Button
            disabled={isDisabled}
            className={cx(
              context === "Prediction"
                ? styles.inferenceBtn
                : [styles.slapShotButton, styles.exportBtn],
            )}
            onClick={() => {
              onSubmit(selectedLayers.map((l) => l.id));
            }}
          >
            {context === "Prediction" && <AiIcon />}
            <span>{context === "Export" ? "Export" : "Run Prediction"} </span>
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ThrottlePopup;
